/**
 * Görünüm ve davranış ayarları sözleşmesi (M6 WP 6.2; migration 0082): menü (bölüm görünürlüğü + grup sırası), liste
 * varsayılanları, dil paketleri. Belge: docs/spec/castinflow/settings.md.
 *
 * MENÜ GÖRÜNÜRLÜĞÜ BİR YETKİ DEĞİLDİR: gizlenen bölüm yalnız menüde görünmez. Kayıt erişimi rol, yetenek ve paylaşım (ACL)
 * ile sunucuda belirlenir; bölümü gizlemek o kayıtlara erişimi kapatmaz (doğrudan bağlantı, arama ve ilişkili kayıtlar
 * çalışmaya devam eder). Arayüz bunu aynı sözcüklerle yazar.
 */
import { z } from 'zod';
import { LIST_QUERY_LIMITS, sortSpecSchema } from './list-query.js';
import { SECTIONS } from './org.js';
import { M2_CORE_RECORD_TYPES } from './records.js';
// ---------------------------------------------------------------------------------------------------------------------
// Menü
// ---------------------------------------------------------------------------------------------------------------------
export const SECTION_LABELS = {
    newsfeed: 'Haber akışı',
    leads: 'Müşteri adayları',
    opportunities: 'Fırsatlar',
    proposals: 'Teklifler',
    orders: 'Siparişler',
    purchase_orders: 'Tedarikçi siparişleri',
    contracts: 'Sözleşmeler',
    dashboard: 'Performans monitörü',
    goals: 'Hedefler',
    activities: 'Aktiviteler',
    calendar: 'Takvim',
    tickets: 'Destek talepleri',
    forms: 'Formlar',
    contacts: 'Kişiler',
    companies: 'Şirketler',
    products: 'Ürünler',
    files: 'Dosyalar',
    users: 'Kullanıcılar',
    automation: 'Otomasyon',
};
/**
 * Menüden gizlenebilen bölümler. Dışarıda kalanlar:
 *  - `files`: bugün ayrı bir menü öğesi yoktur (dosyalar kayıtların içindedir) — işlevi olmayan bir anahtar gösterilmez.
 *  - `users`, `automation`: yönetim araçlarıdır; menüdeki görünürlükleri yalnız yetenekle (`users.manage`, `settings.manage`)
 *    belirlenir, gizleme ayarına konu olmaz (yönetici kendi yönetim menüsünü kapatamaz).
 */
const NON_HIDEABLE = ['files', 'users', 'automation'];
export const MENU_SECTIONS = SECTIONS.filter((s) => !NON_HIDEABLE.includes(s));
/** Menü grupları (apps/web/src/shell/navigation.tsx `NAVIGATION` kimlikleri). İlk ve son grup sabittir. */
export const NAV_GROUPS = [
    'main',
    'sales',
    'operations',
    'performance',
    'support',
    'forms',
    'records',
    'admin',
];
/** Sırası değiştirilebilen gruplar ("Genel" başta, "Yönetim" sonda sabit kalır) */
export const NAV_MOVABLE_GROUPS = [
    'sales',
    'operations',
    'performance',
    'support',
    'forms',
    'records',
];
export const NAV_GROUP_LABELS = {
    sales: 'Satış',
    operations: 'Operasyon',
    performance: 'Performans',
    support: 'Destek',
    forms: 'Formlar',
    records: 'Kayıtlar',
};
const groupOrderSchema = z
    .array(z.enum(NAV_MOVABLE_GROUPS))
    .length(NAV_MOVABLE_GROUPS.length)
    .refine((v) => new Set(v).size === v.length, 'Grup sırası her grubu bir kez içermeli');
export const navigationSettingsSchema = z
    .object({
    /** Taşınabilir grupların sırası (tam permütasyon) */
    groupOrder: groupOrderSchema,
    /** Çalışma alanının tamamında menüden gizlenen bölümler (yönetici rolü her zaman görür) */
    hiddenSections: z
        .array(z.enum(SECTIONS))
        .max(SECTIONS.length)
        .refine((v) => new Set(v).size === v.length, 'Bölüm yinelenemez')
        .refine((v) => v.every((s) => MENU_SECTIONS.includes(s)), 'Bu bölüm menüden gizlenemez'),
})
    .strict();
export const DEFAULT_NAVIGATION_SETTINGS = {
    groupOrder: [...NAV_MOVABLE_GROUPS],
    hiddenSections: [],
};
/** Departman bölüm görünürlüğü (okuma): bölüm → görünür mü (kayıt yoksa görünür) */
export const departmentSectionsSchema = z.object({
    sections: z.partialRecord(z.enum(SECTIONS), z.boolean()),
});
/**
 * Menü grup sırasını uygular: `main` başta, `admin` sonda; ortadaki gruplar verilen sırayla. Sırada olmayan (ileride
 * eklenecek) grup sona — `admin`'den önce — eklenir; bilinmeyen kimlik yok sayılır.
 */
export function orderNavGroups(groups, order) {
    const rank = (id) => {
        if (id === 'main')
            return -1;
        if (id === 'admin')
            return Number.MAX_SAFE_INTEGER;
        const i = order.indexOf(id);
        return i < 0 ? order.length : i;
    };
    return groups
        .map((g, i) => ({ g, i }))
        .sort((a, b) => rank(a.g.id) - rank(b.g.id) || a.i - b.i)
        .map((x) => x.g);
}
// ---------------------------------------------------------------------------------------------------------------------
// Dil paketleri
// ---------------------------------------------------------------------------------------------------------------------
export const UI_LANGUAGES = ['tr', 'en'];
export const OUTPUT_LANGUAGES = ['tr', 'en'];
export const LANGUAGE_NAMES = { tr: 'Türkçe', en: 'English' };
/**
 * Belge çıktısı (teklif / sipariş / tedarikçi siparişi PDF) etiketleri. Türkçe paket TAMDIR ve kaynak pakettir; diğer paketler
 * kısmi olabilir — eksik anahtar Türkçeye düşer (`outputLabels`). Kullanıcının yazdığı içerik (ürün adı, açıklama, koşullar)
 * çevrilmez.
 */
export const OUTPUT_LABELS_TR = {
    docProposal: 'Teklif',
    docOrder: 'Sipariş',
    docPurchaseOrder: 'Tedarikçi siparişi',
    coverTitle: 'Fiyat teklifi',
    revision: 'Revizyon',
    proposalDate: 'Teklif tarihi',
    proposalUntil: 'Geçerlilik',
    orderDate: 'Sipariş tarihi',
    orderUntil: 'Teslim tarihi',
    currency: 'Para birimi',
    customer: 'Müşteri',
    supplier: 'Tedarikçi',
    contactPerson: 'İlgili kişi',
    deliveryAddress: 'Teslim adresi',
    phoneShort: 'Tel',
    colItem: 'Ürün / açıklama',
    colQuantity: 'Miktar',
    colUnitPrice: 'Birim fiyat',
    colDiscount: 'İndirim',
    colVat: 'KDV',
    colAmount: 'Tutar',
    subtotal: 'Ara toplam',
    lineDiscounts: 'Satır indirimleri',
    documentDiscount: 'Genel indirim',
    importCost: 'İthalat masrafı',
    total: 'Toplam',
    totalExclVat: 'KDV hariç toplam',
    secondTax: 'İkinci vergi',
    vat: 'KDV',
    grandTotal: 'Genel toplam',
    paymentTerms: 'Ödeme koşulları',
    deliveryTerms: 'Teslim koşulları',
    notes: 'Notlar',
    vatIncluded: 'Fiyatlara KDV dahildir',
    vatExcluded: 'Fiyatlara KDV hariçtir',
    vatNone: 'Vergi uygulanmaz',
    page: 'Sayfa',
};
/** İngilizce çıktı paketi (belge etiketleri). Eksik anahtar Türkçeye düşer. */
const OUTPUT_LABELS_EN = {
    docProposal: 'Quotation',
    docOrder: 'Order',
    docPurchaseOrder: 'Purchase order',
    coverTitle: 'Quotation',
    revision: 'Revision',
    proposalDate: 'Date',
    proposalUntil: 'Valid until',
    orderDate: 'Order date',
    orderUntil: 'Delivery date',
    currency: 'Currency',
    customer: 'Customer',
    supplier: 'Supplier',
    contactPerson: 'Contact',
    deliveryAddress: 'Delivery address',
    phoneShort: 'Tel',
    colItem: 'Item / description',
    colQuantity: 'Quantity',
    colUnitPrice: 'Unit price',
    colDiscount: 'Discount',
    colVat: 'VAT',
    colAmount: 'Amount',
    subtotal: 'Subtotal',
    lineDiscounts: 'Line discounts',
    documentDiscount: 'Discount',
    importCost: 'Import cost',
    total: 'Total',
    totalExclVat: 'Total excl. VAT',
    secondTax: 'Additional tax',
    vat: 'VAT',
    grandTotal: 'Grand total',
    paymentTerms: 'Payment terms',
    deliveryTerms: 'Delivery terms',
    notes: 'Notes',
    vatIncluded: 'Prices include VAT',
    vatExcluded: 'Prices exclude VAT',
    vatNone: 'No tax applied',
    page: 'Page',
};
export const OUTPUT_LABEL_PACKS = {
    tr: OUTPUT_LABELS_TR,
    en: OUTPUT_LABELS_EN,
};
/** Paket çözümleme: seçilen dilin etiketi, yoksa (ya da boşsa) Türkçesi. Bilinmeyen dil Türkçedir. */
export function outputLabels(language) {
    const pack = language !== null && language !== undefined && language in OUTPUT_LABEL_PACKS
        ? OUTPUT_LABEL_PACKS[language]
        : OUTPUT_LABELS_TR;
    const out = { ...OUTPUT_LABELS_TR };
    for (const key of Object.keys(OUTPUT_LABELS_TR)) {
        const v = pack[key];
        if (typeof v === 'string' && v.trim() !== '')
            out[key] = v;
    }
    return out;
}
/** Paketin kapsamı: çevrilmiş anahtar sayısı / toplam (ayar ekranı eksik çeviriyi açıkça gösterir). */
export function packCoverage(pack, total) {
    const translated = Object.values(pack).filter((v) => typeof v === 'string' && v.trim() !== '').length;
    return { translated: Math.min(translated, total), total };
}
export const languageSettingsInputSchema = z
    .object({
    uiLanguage: z.enum(UI_LANGUAGES),
    outputLanguage: z.enum(OUTPUT_LANGUAGES),
})
    .strict();
export const languageSettingsSchema = z.object({
    uiLanguage: z.enum(UI_LANGUAGES),
    outputLanguage: z.enum(OUTPUT_LANGUAGES),
    outputPacks: z.array(z.object({
        language: z.enum(OUTPUT_LANGUAGES),
        name: z.string(),
        translated: z.number().int(),
        total: z.number().int(),
    })),
});
// ---------------------------------------------------------------------------------------------------------------------
// Kabuk ayarları (her kullanıcı kendi görünümünü okur)
// ---------------------------------------------------------------------------------------------------------------------
export const shellSettingsSchema = z.object({
    /** Çağıranın menüsünde görünen bölümler: rol ∩ departman görünürlüğü ∩ çalışma alanı görünürlüğü */
    sections: z.array(z.enum(SECTIONS)),
    groupOrder: z.array(z.enum(NAV_MOVABLE_GROUPS)),
    uiLanguage: z.enum(UI_LANGUAGES),
});
// ---------------------------------------------------------------------------------------------------------------------
// Liste varsayılanları (çalışma alanı × kayıt tipi)
// ---------------------------------------------------------------------------------------------------------------------
export const LIST_PAGE_SIZES = [10, 25, 50, 100];
/** Çalışma alanı varsayılanı yoksa arayüzün sayfa boyutu */
export const LIST_DEFAULT_PAGE_SIZE = 25;
export const LIST_DEFAULT_RECORD_TYPES = M2_CORE_RECORD_TYPES;
const pageSizeSchema = z
    .number()
    .int()
    .refine((n) => LIST_PAGE_SIZES.includes(n), 'Geçersiz sayfa boyutu');
/**
 * Çalışma alanı liste varsayılanı. Öncelik: istek (kullanıcının o anki seçimi) > kişisel kayıtlı liste > BU AYAR > sistem
 * varsayılanı. Kolon ve sıralama alanları kaydedilirken alan kataloğuna göre doğrulanır; okurken çağıranın göremediği alan
 * sessizce düşer (değer sızmaz).
 */
export const listDefaultsInputSchema = z
    .object({
    columns: z
        .array(z.string().min(1).max(80))
        .min(1)
        .max(LIST_QUERY_LIMITS.maxColumns)
        .refine((v) => new Set(v).size === v.length, 'Kolon yinelenemez'),
    sort: sortSpecSchema.nullable(),
    pageSize: pageSizeSchema,
})
    .strict();
export const listDefaultsItemSchema = z.object({
    recordType: z.enum(M2_CORE_RECORD_TYPES),
    /** Çalışma alanı bu tip için varsayılan kaydetmiş mi (değilse değerler sistem varsayılanıdır) */
    customized: z.boolean(),
    columns: z.array(z.string()),
    sort: sortSpecSchema.nullable(),
    pageSize: z.number().int(),
});
export const listDefaultsResponseSchema = z.object({ items: z.array(listDefaultsItemSchema) });
export function isListDefaultRecordType(v) {
    return M2_CORE_RECORD_TYPES.includes(v);
}
//# sourceMappingURL=ui-settings.js.map